import type { ComponentChildren } from "preact";
import { effect, signal } from "@preact/signals";
import { useLocation } from "preact-iso";
import { useEffect, useRef, useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import {
  apiClient,
  botsRoute,
  freeTierCap,
  imageGenerationAgentRunMultiplier,
  isEntityInCurrentPeriod,
  isLlmBillingActive,
  resetText,
  tierByName,
} from "../../constants.ts";
import { AdminsConfig } from "./adminsConfig.tsx";
import { AliceAndBotConfig } from "./aliceAndBotConfig.tsx";
import { BehaviorConfig, PromptConfig } from "./basicConfig.tsx";
import { showWidget, TalkToBotSection } from "./chatWidget.tsx";
import {
  BotNotFound,
  Button,
  Collapsible,
  customConfirm,
  inputBaseClass,
  PageSpinner,
  ViewConversationsButton,
  WebEmbedSnippetWithInputs,
} from "./components.tsx";
import {
  InjectContextByUserId,
  type PreferredNetwork,
} from "./injectContextUi.tsx";
import { CustomToolsConfig } from "./customToolsConfig.tsx";
import { CommunitySkillsConfig } from "./communitySkillsConfig.tsx";
import { CustomSkillsConfig } from "./customSkillsConfig.tsx";
import { CustomRecorderWebhookConfig } from "./customRecorderWebhookConfig.tsx";
import { transact, tx, useAuth, useInfiniteQuery, useQuery } from "./db.ts";
import { EditableText } from "./editableText.tsx";
import { FacebookMessengerConfig } from "./facebookMessengerConfig.tsx";
import { InstagramConfig } from "./instagramConfig.tsx";
import { Files } from "./files.tsx";
import { ForwardEmailConfig } from "./forwardEmailConfig.tsx";
import { AgentmailConfig } from "./agentmailConfig.tsx";
import { ResendConfig } from "./resendConfig.tsx";
import { GmailConfig } from "./gmailConfig.tsx";
import { ApiTokensSection } from "./apiTokensSection.tsx";
import { InviteLink, OpenWebChatButton } from "./inviteLink.tsx";
import { useActiveUser } from "./loginHooks.ts";
import { usePageMeta } from "./pageMeta.ts";
import { settingsPath } from "./paths.ts";
import { Threads } from "./threads.tsx";
import { readableError } from "./basicConfigPartials.tsx";
import { SecretsConfig } from "./secretsConfig.tsx";
import { SlackConfig } from "./slackConfig.tsx";

import { Tasks } from "./scheduledActions.tsx";
import { TeamsConfig } from "./teamsConfig.tsx";
import { TelegramConfig } from "./telegramConfig.tsx";

import { WhatsappForBusinessConfig } from "./whatsappForBusinessConfig.tsx";
import { WhatsappPrivateConfig } from "./whatsappPrivateConfig.tsx";

import { preferredNetworkValues } from "../../scheduledActionSchema.ts";

type Category =
  | "prompt"
  | "behavior"
  | "overview"
  | "automation"
  | "channels"
  | "integrations"
  | "skills-tools"
  | "api-access"
  | "secrets"
  | "danger";

const categoryTabs: readonly {
  readonly id: Category;
  readonly label: string;
}[] = [
  { id: "overview", label: "Overview" },
  { id: "prompt", label: "Prompt" },
  { id: "behavior", label: "Behavior" },
  { id: "automation", label: "Automation" },
  { id: "channels", label: "Channels" },
  { id: "integrations", label: "Files" },
  { id: "skills-tools", label: "Skills & Tools" },
  { id: "api-access", label: "API Access" },
  { id: "secrets", label: "Environment" },
  { id: "danger", label: "Danger zone" },
];

const matchesSearch =
  (filter: string) =>
  ({ title, keywords }: { title: string; keywords: readonly string[] }) =>
    title.toLowerCase().includes(filter) ||
    keywords.some((k) => k.includes(filter));

const categoryLabel = (id: Category) =>
  categoryTabs.find((tab) => tab.id === id)?.label ?? id;

const sectionDomId = (title: string) =>
  `bot-settings-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

type SearchableSection = {
  title: string;
  keywords: readonly string[];
  categories: readonly Category[];
  content: ComponentChildren;
};

type SectionMetadata = Omit<SearchableSection, "content">;

const sectionMetadata: readonly SectionMetadata[] = [
  {
    title: "Prompt & Instructions",
    keywords: ["prompt", "model", "fast", "instructions", "system prompt"],
    categories: ["prompt"],
  },
  {
    title: "Behavior & Settings",
    keywords: [
      "hallucination",
      "group chat",
      "error message",
      "deprecated",
      "deprecation",
      "blocked",
      "image",
      "ads",
      "isolate",
      "ai provider",
      "kimi",
      "moonshot",
      "gemini",
      "google",
      "smarter model",
      "code execution",
      "timezone",
      "promotional",
      "isolate users",
    ],
    categories: ["behavior"],
  },
  {
    title: "Admins",
    keywords: ["admin", "team", "access", "permissions"],
    categories: ["danger"],
  },
  {
    title: "Actions",
    keywords: ["talk", "chat", "invite", "conversations"],
    categories: ["overview"],
  },
  {
    title: "Usage",
    keywords: ["billing", "usage", "messages", "agent runs", "plan"],
    categories: ["overview"],
  },
  {
    title: "Inject Context",
    keywords: ["inject", "context", "user data"],
    categories: ["automation"],
  },
  {
    title: "Scheduled Actions",
    keywords: [
      "scheduled",
      "actions",
      "cron",
      "recurring",
      "interval",
      "reminder",
    ],
    categories: ["automation"],
  },
  {
    title: "Threads",
    keywords: ["threads", "projects", "kanban", "board"],
    categories: ["automation"],
  },
  {
    title: "Web",
    keywords: ["web", "embed", "widget", "snippet", "website", "iframe"],
    categories: ["channels"],
  },
  {
    title: "Alice & Bot Network",
    keywords: ["alice", "bot", "network", "p2p"],
    categories: ["channels"],
  },
  {
    title: "Telegram",
    keywords: ["telegram", "bot token"],
    categories: ["channels"],
  },
  {
    title: "Slack",
    keywords: ["slack", "workspace", "events api", "signing secret"],
    categories: ["channels"],
  },
  {
    title: "Microsoft Teams",
    keywords: ["teams", "microsoft", "bot framework", "azure"],
    categories: ["channels"],
  },
  {
    title: "WhatsApp",
    keywords: ["whatsapp", "private", "phone", "pairing", "supergreen"],
    categories: ["channels"],
  },
  {
    title: "WhatsApp for Business",
    keywords: ["whatsapp", "business", "waba", "meta"],
    categories: ["channels"],
  },
  {
    title: "Facebook Messenger",
    keywords: ["facebook", "messenger", "meta", "page"],
    categories: ["channels"],
  },
  {
    title: "Instagram DM",
    keywords: ["instagram", "dm", "meta", "page"],
    categories: ["channels"],
  },
  {
    title: "Forward Email",
    keywords: ["email", "forward", "smtp"],
    categories: ["channels"],
  },
  {
    title: "AgentMail",
    keywords: ["agentmail", "email", "inbox"],
    categories: ["channels"],
  },
  {
    title: "Resend",
    keywords: ["resend", "email", "inbox"],
    categories: ["channels"],
  },
  {
    title: "Gmail",
    keywords: ["gmail", "email", "oauth"],
    categories: ["channels"],
  },
  {
    title: "Files",
    keywords: ["files", "upload", "documents"],
    categories: ["integrations"],
  },
  {
    title: "Custom Tools",
    keywords: [
      "custom",
      "tools",
      "api",
      "webhook",
      "function",
      "endpoint",
      "openapi",
    ],
    categories: ["skills-tools"],
  },
  {
    title: "Custom Skills",
    keywords: ["custom", "skills", "prompt", "knowledge"],
    categories: ["skills-tools"],
  },
  {
    title: "Community Skills",
    keywords: ["community", "skills", "marketplace", "shared"],
    categories: ["skills-tools"],
  },
  {
    title: "Bot API Tokens",
    keywords: ["api", "token", "bot token", "access token", "automation"],
    categories: ["api-access"],
  },
  {
    title: "Custom Recorder Webhook",
    keywords: ["recorder", "webhook", "custom", "logging", "events"],
    categories: ["api-access"],
  },
  {
    title: "Environment",
    keywords: [
      "secrets",
      "env",
      "environment",
      "api key",
      "credentials",
      "variables",
    ],
    categories: ["secrets"],
  },
  {
    title: "Danger zone",
    keywords: ["delete", "nuke", "reset", "destroy", "remove", "wipe"],
    categories: ["danger"],
  },
];

const botSearchResults = (
  bots: readonly { id: string; name: string }[],
  searchQuery: string,
) =>
  bots
    .filter(({ name }) =>
      (name || "").toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

const settingSearchResults = (
  sections: readonly SectionMetadata[],
  searchQuery: string,
) => {
  const trimmedFilter = searchQuery.trim().toLowerCase();
  return trimmedFilter ? sections.filter(matchesSearch(trimmedFilter)) : [];
};

const initialDisplayLimit = 15;

const BotSearchDialog = (
  {
    bots,
    sections,
    onSelectBot,
    onSelectSection,
    onClose,
    canLoadMoreBots,
    loadMoreBots,
    isLoading,
  }: {
    bots: readonly { id: string; name: string }[];
    sections: readonly SectionMetadata[];
    onSelectBot: (id: string) => void;
    onSelectSection: (title: string, category: Category) => void;
    onClose: () => void;
    canLoadMoreBots: boolean;
    loadMoreBots: () => void;
    isLoading?: boolean;
  },
) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [showAllBots, setShowAllBots] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const matchingBots = botSearchResults(bots, searchQuery);
  const isSearching = searchQuery.trim().length > 0;
  const displayedBots = isSearching || showAllBots
    ? matchingBots
    : matchingBots.slice(0, initialDisplayLimit);
  const matchingSections = settingSearchResults(sections, searchQuery);
  const results = [
    ...displayedBots.map((bot) => ({ type: "bot" as const, bot })),
    ...matchingSections.map((section) => ({
      type: "section" as const,
      section,
    })),
  ];
  const hasMoreBots = !isSearching && !showAllBots &&
    matchingBots.length > initialDisplayLimit;

  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchQuery, showAllBots]);

  useEffect(() => {
    if (!results[highlightedIndex]) {
      setHighlightedIndex(Math.max(results.length - 1, 0));
    }
  }, [highlightedIndex, results.length]);

  useEffect(() => {
    const highlighted = listRef.current?.querySelector<HTMLElement>(
      `[data-result-index="${highlightedIndex}"]`,
    );
    highlighted?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  useEffect(() => {
    const el = listRef.current;
    if (!el || !canLoadMoreBots) return;
    if (el.scrollHeight <= el.clientHeight) {
      loadMoreBots();
    }
  }, [canLoadMoreBots, loadMoreBots, results.length]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((index) => Math.min(index + 1, results.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((index) => Math.max(index - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const highlighted = results[highlightedIndex];
      if (!highlighted) return;
      if (highlighted.type === "bot") {
        onSelectBot(highlighted.bot.id);
        return;
      }
      onSelectSection(
        highlighted.section.title,
        highlighted.section.categories[0],
      );
      return;
    }
    if (e.key === "Escape") {
      onClose();
    }
  };

  const handleScroll = () => {
    const el = listRef.current;
    if (!el || !canLoadMoreBots) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      loadMoreBots();
    }
  };

  let resultIndex = -1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[16vh]"
      onClick={onClose}
    >
      <div className="fixed inset-0 bg-black/50" />
      <div
        className="relative w-full max-w-2xl bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onInput={(e) => setSearchQuery(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search bots or settings..."
          className={`w-full border-0 border-b border-border-subtle-light dark:border-border-subtle rounded-t-xl rounded-b-none focus:ring-0 ${inputBaseClass}`}
        />
        <div
          ref={listRef}
          className="max-h-[70vh] overflow-y-auto p-2"
          onScroll={handleScroll}
        >
          <div className="px-3 py-2 text-xs font-semibold text-text-tertiary-light dark:text-text-tertiary uppercase tracking-wide">
            Bots
          </div>
          {displayedBots.map((bot) => {
            resultIndex += 1;
            return (
              <button
                key={bot.id}
                type="button"
                data-result-index={resultIndex}
                onMouseEnter={() => setHighlightedIndex(resultIndex)}
                onClick={() => onSelectBot(bot.id)}
                className={`w-full text-left px-4 py-2.5 text-sm rounded-lg cursor-pointer ${
                  highlightedIndex === resultIndex
                    ? "bg-accent text-white font-bold"
                    : "hover:bg-surface-overlay-light dark:hover:bg-surface-overlay text-text-primary-light dark:text-text-primary"
                }`}
              >
                {bot.name || "Untitled bot"}
              </button>
            );
          })}
          {hasMoreBots && (
            <button
              type="button"
              onClick={() => setShowAllBots(true)}
              className="w-full text-center px-3 py-2 text-sm text-accent hover:bg-surface-overlay-light dark:hover:bg-surface-overlay rounded-lg cursor-pointer"
            >
              Show all ({matchingBots.length - initialDisplayLimit} more)
            </button>
          )}
          {matchingSections.length > 0 && (
            <>
              <div className="px-3 py-2 mt-2 text-xs font-semibold text-text-tertiary-light dark:text-text-tertiary uppercase tracking-wide">
                Settings
              </div>
              {matchingSections.map((section) => {
                resultIndex += 1;
                return (
                  <button
                    key={section.title}
                    type="button"
                    data-result-index={resultIndex}
                    onMouseEnter={() => setHighlightedIndex(resultIndex)}
                    onClick={() =>
                      onSelectSection(section.title, section.categories[0])}
                    className={`w-full text-left px-4 py-2.5 text-sm rounded-lg cursor-pointer ${
                      highlightedIndex === resultIndex
                        ? "bg-accent text-white font-bold"
                        : "hover:bg-surface-overlay-light dark:hover:bg-surface-overlay text-text-primary-light dark:text-text-primary"
                    }`}
                  >
                    <div>{section.title}</div>
                    <div
                      className={`text-xs ${
                        highlightedIndex === resultIndex
                          ? "text-white/80"
                          : "text-text-tertiary-light dark:text-text-tertiary"
                      }`}
                    >
                      {categoryLabel(section.categories[0])}
                    </div>
                  </button>
                );
              })}
            </>
          )}
          {results.length === 0 && isLoading && (
            <div className="px-4 py-6 text-sm text-text-secondary-light dark:text-text-secondary text-center">
              Loading bots...
            </div>
          )}
          {results.length === 0 && !isLoading && (
            <div className="px-4 py-6 text-sm text-text-secondary-light dark:text-text-secondary text-center">
              No results found
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ConfirmDestructiveAction = (
  { botName, onConfirm, buttonLabel, description, loading, successMessage }: {
    botName: string;
    onConfirm: () => Promise<unknown>;
    buttonLabel: string;
    description: string;
    loading: string;
    successMessage: string;
  },
) => {
  const [confirmName, setConfirmName] = useState("");
  const isMatch = confirmName === botName;
  return (
    <div className="flex flex-col gap-2 mb-2">
      <div className="text-xs text-text-secondary-light dark:text-text-secondary mb-1">
        {description}
      </div>
      <div className="text-xs text-text-secondary-light dark:text-text-secondary">
        Type <strong>{botName}</strong> to confirm:
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={confirmName}
          onChange={(e) => setConfirmName(e.currentTarget.value)}
          placeholder={botName}
          className={`flex-1 px-3 py-2 border rounded max-w-md ${inputBaseClass}`}
        />
        <Button
          onClick={() => {
            toast.promise(onConfirm().then(() => setConfirmName("")), {
              loading,
              success: successMessage,
              error: (err) => `Failed: ${err.message}`,
            });
          }}
          className="bg-danger hover:bg-danger-hover"
          disabled={!isMatch}
        >
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
};

const DeleteAllMemory = (
  { botId, botName, onNuked }: {
    botId: string;
    botName: string;
    onNuked: () => void;
  },
) => {
  const auth = useAuth();
  const [deleteViewChat, setDeleteViewChat] = useState(false);
  return (
    <div className="flex flex-col gap-2 mb-2">
      <label className="text-sm font-medium">
        Nuke bot (reset everything)
      </label>
      <div className="flex items-center gap-2 mb-1 text-sm">
        <input
          type="checkbox"
          id={`delete-view-chat-${botId}`}
          checked={deleteViewChat}
          onChange={(e) => setDeleteViewChat(e.currentTarget.checked)}
          className="rounded border-border-subtle-light dark:border-border-subtle text-primary focus:ring-primary h-4 w-4"
        />
        <label
          htmlFor={`delete-view-chat-${botId}`}
          className="text-xs text-text-secondary-light dark:text-text-secondary select-none"
        >
          Also delete view-chat conversations
        </label>
      </div>
      <ConfirmDestructiveAction
        botName={botName}
        buttonLabel="Nuke bot"
        description="This will delete all conversation history, memory, threads, tasks, persistent VMs, and secrets for this bot. The bot will be completely reset."
        loading="Nuking bot..."
        successMessage="Bot nuked successfully"
        onConfirm={() =>
          apiClient({
            endpoint: "delete-all-memory",
            token: auth.user?.refresh_token ?? "",
            payload: { botId, deleteViewChat },
          }).then(onNuked)}
      />
    </div>
  );
};

const DeleteBotEntirely = (
  { bot, token }: {
    bot: { id: string; name: string };
    token: string | null;
  },
) => {
  const [deleteViewChat, setDeleteViewChat] = useState(false);
  return (
    <div>
      <label className="text-sm font-medium">
        Delete Bot entirely
      </label>
      <div className="flex items-center gap-2 mb-1 text-sm mt-1">
        <input
          type="checkbox"
          id={`delete-bot-view-chat-${bot.id}`}
          checked={deleteViewChat}
          onChange={(e) => setDeleteViewChat(e.currentTarget.checked)}
          className="rounded border-border-subtle-light dark:border-border-subtle text-primary focus:ring-primary h-4 w-4"
        />
        <label
          htmlFor={`delete-bot-view-chat-${bot.id}`}
          className="text-xs text-text-secondary-light dark:text-text-secondary select-none"
        >
          Also delete view-chat conversations
        </label>
      </div>
      <ConfirmDestructiveAction
        botName={bot.name}
        buttonLabel="Delete Bot entirely"
        description="This will destroy all VMs, delete all data, and remove the bot. This action cannot be undone."
        loading="Deleting bot..."
        successMessage={`Bot ${bot.name} has been deleted.`}
        onConfirm={() => {
          selectedBot.value = "";
          showWidget.value = false;
          return apiClient({
            endpoint: "delete-bot",
            token: token ?? "",
            payload: { botId: bot.id, deleteViewChat },
          });
        }}
      />
    </div>
  );
};

const DeleteUserMemory = ({ botId }: { botId: string }) => {
  const [userId, setUserId] = useState("");
  const auth = useAuth();
  return (
    <div className="flex flex-col gap-2 mb-2">
      <label className="text-sm font-medium">
        Delete user memory
      </label>
      <div className="text-xs text-text-secondary-light dark:text-text-secondary mb-1">
        Enter the channel-specific identifier:
        <br />
        • WhatsApp: phone number (e.g., 972501234567)
        <br />
        • Email: email address
        <br />
        • Telegram: username
        <br />
        • Slack: Slack user or DM ID
        <br />
        • Teams: Teams user ID
        <br />
        • Alice-and-Bot: public sign key
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={userId}
          onChange={(e) => setUserId(e.currentTarget.value)}
          placeholder="e.g., 972501234567 or user@example.com"
          className={`flex-1 ${inputBaseClass}`}
        />
        <Button
          onClick={async () => {
            if (!userId.trim()) {
              toast.error("Please enter a user ID");
              return;
            }
            if (
              await customConfirm(
                `Are you sure you want to delete all memory and conversation history for user ${userId}? This action cannot be undone.`,
              )
            ) {
              toast.promise(
                apiClient({
                  endpoint: "delete-user-memory",
                  token: auth.user?.refresh_token ?? "",
                  payload: { botId, userId: userId.trim() },
                }),
                {
                  loading: "Deleting user memory...",
                  success: () => {
                    setUserId("");
                    return "User memory and conversation history deleted successfully";
                  },
                  error: (err) =>
                    `Failed to delete user memory: ${
                      err.error || err.message || "Unknown error"
                    }`,
                },
              );
            }
          }}
          className="bg-danger hover:bg-danger-hover"
          disabled={!userId.trim()}
        >
          Delete Memory
        </Button>
      </div>
      <p className="text-xs text-text-tertiary-light dark:text-text-tertiary">
        This is equivalent to the user sending {resetText}
        in a conversation. It deletes both the user's long-term memory and their
        conversation history.
      </p>
    </div>
  );
};

const readUrlBotId = () =>
  typeof globalThis.location !== "undefined"
    ? new URLSearchParams(globalThis.location.search).get("botId")
    : null;

export const selectedBot = signal<string>(readUrlBotId() ?? "");
const botSwitcherOpen = signal(false);
type HighlightedSection = { title: string; category: Category } | null;
const highlightedSectionSignal = signal<HighlightedSection>(null);

const switcherSections = sectionMetadata.map((section) => ({
  ...section,
  content: null,
}));

const botListQuery = (userId: string) => ({
  bots: {
    $: {
      where: { admins: userId },
      limit: botPageSize,
      order: { serverCreatedAt: "desc" },
    },
  },
});

const openBotSwitcher = () => {
  botSwitcherOpen.value = true;
};

const closeBotSwitcher = () => {
  botSwitcherOpen.value = false;
};

const readInjectContextParams = () => {
  if (typeof globalThis.location === "undefined") {
    return {
      injectContext: false,
      network: "" as const,
      conversationId: "",
    };
  }
  const params = new URLSearchParams(globalThis.location.search);
  const networkRaw = params.get("network") ?? "";
  return {
    injectContext: params.get("injectContext") === "1",
    network: (preferredNetworkValues as readonly string[]).includes(networkRaw)
      ? (networkRaw as (typeof preferredNetworkValues)[number])
      : "" as const,
    conversationId: params.get("conversationId") ?? params.get("userId") ?? "",
  };
};

effect(() => {
  if (!selectedBot.value) return;
  if (globalThis.location?.pathname !== botsRoute) return;
  const params = new URLSearchParams(globalThis.location.search);
  if (params.get("botId") !== selectedBot.value) {
    params.set("botId", selectedBot.value);
    const newUrl = `${globalThis.location.pathname}?${params.toString()}`;
    globalThis.history.replaceState({}, "", newUrl);
  }
});

export const BotSwitcher = () => {
  const authUserId = useAuth().user?.id;
  const activeUser = useActiveUser();
  const { route } = useLocation();
  const {
    data: botsData,
    isLoading: botsLoading,
    canLoadNextPage,
    loadNextPage,
  } = useInfiniteQuery(
    botSwitcherOpen.value && activeUser?.id
      ? botListQuery(activeUser.id)
      : null,
  );
  const bots = botsData?.bots ?? [];

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.code === "KeyK")) {
        e.preventDefault();
        openBotSwitcher();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (botSwitcherOpen.value && canLoadNextPage) loadNextPage();
  }, [botSwitcherOpen.value, canLoadNextPage]);

  if (!botSwitcherOpen.value) return null;

  const selectBot = (id: string) => {
    showWidget.value = false;
    selectedBot.value = id;
    highlightedSectionSignal.value = null;
    closeBotSwitcher();
    route(`${botsRoute}?botId=${id}`);
  };

  const selectSection = (title: string, category: Category) => {
    const targetBotId = selectedBot.value || bots[0]?.id;
    if (!targetBotId) {
      closeBotSwitcher();
      route(botsRoute);
      return;
    }
    showWidget.value = false;
    selectedBot.value = targetBotId;
    highlightedSectionSignal.value = { title, category };
    closeBotSwitcher();
    route(`${botsRoute}?botId=${targetBotId}`);
  };

  return (
    <BotSearchDialog
      bots={bots}
      sections={switcherSections}
      onSelectBot={selectBot}
      onSelectSection={selectSection}
      onClose={closeBotSwitcher}
      canLoadMoreBots={canLoadNextPage}
      loadMoreBots={loadNextPage}
      isLoading={!!authUserId && (!activeUser?.id || botsLoading || !botsData)}
    />
  );
};

const createNewBot = (apiToken: string, name: string, prompt: string) =>
  toast.promise(
    apiClient({
      endpoint: "create-bot",
      token: apiToken,
      payload: { name, prompt },
    }).then((res) => {
      if (res.success) {
        selectedBot.value = res.botId;
        return "Created new bot.";
      }
      throw new Error(res.error);
    }),
    {
      loading: "Creating bot...",
      success: (res) => res,
      error: (err: Error) => `Could not create bot: ${err.message}`,
    },
  );

const NewBotWizard = ({ onCancel, onFinish }: {
  onCancel: () => void;
  onFinish: () => void;
}) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const userApiToken = useAuth().user?.refresh_token;
  const botId = selectedBot.value;
  const { data: botData } = useQuery({
    bots: { $: { where: { id: botId } } },
  });
  const bot = botData?.bots?.[0];

  const handleCreate = async () => {
    if (!userApiToken) return;
    try {
      setIsCreating(true);
      await createNewBot(userApiToken, name, prompt);
      // selectedBot is set inside createNewBot
      setStep(3);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="p-4 bg-surface-raised-light dark:bg-surface-raised rounded-xl border border-border-subtle-light dark:border-border-subtle shadow-lg w-full max-w-2xl mx-auto">
      {step === 1 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-xl font-display font-bold text-text-primary-light dark:text-text-primary">
            Create a new bot
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary">
            What would you like to name your bot? You can change this later.
          </p>
          <input
            type="text"
            dir="auto"
            value={name}
            onInput={(e) => setName(e.currentTarget.value)}
            placeholder="e.g., Customer Support Bot"
            className={inputBaseClass}
          />
          <div className="flex gap-2 justify-end">
            <Button
              onClick={() => {
                showWidget.value = false;
                onCancel();
              }}
              className="bg-surface-overlay-light dark:bg-surface-overlay text-text-primary-light dark:text-text-primary hover:bg-border-subtle-light dark:hover:bg-border-subtle"
            >
              Cancel
            </Button>
            <Button
              onClick={() => name && setStep(2)}
              disabled={!name}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      {step === 2 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-xl font-display font-bold text-text-primary-light dark:text-text-primary">
            Set the basic prompt
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary">
            This is the core instruction for your bot. You can change it later.
          </p>
          <textarea
            dir="auto"
            value={prompt}
            onInput={(e) => setPrompt(e.currentTarget.value)}
            placeholder="You are a helpful assistant that answers questions about our products."
            rows={6}
            className={inputBaseClass}
          />
          <div className="flex gap-2 justify-end">
            <Button
              onClick={() => setStep(1)}
              className="bg-surface-overlay-light dark:bg-surface-overlay text-text-primary-light dark:text-text-primary hover:bg-border-subtle-light dark:hover:bg-border-subtle"
              disabled={isCreating}
            >
              Back
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!prompt || !userApiToken || isCreating}
            >
              {isCreating ? "Creating..." : "Create Bot"}
            </Button>
          </div>
        </div>
      )}
      {step === 3 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-xl font-display font-bold text-text-primary-light dark:text-text-primary">
            Configure user isolation
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary text-sm">
            Should this bot treat each conversation as completely isolated
            (customer support, public bots), or can it share context across
            conversations (personal/team assistant)?
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <button
              type="button"
              onClick={() => {
                if (bot) {
                  transact(tx.bots[bot.id].update({ isolateUsers: true }));
                }
              }}
              className={`p-4 rounded-xl border text-left flex flex-col gap-2 transition-all cursor-pointer ${
                bot?.isolateUsers
                  ? "border-accent bg-accent/5 dark:bg-accent/10 shadow-md"
                  : "border-border-subtle-light dark:border-border-subtle hover:border-accent/50 bg-surface-overlay-light dark:bg-surface-overlay"
              }`}
            >
              <div className="font-bold text-sm text-text-primary-light dark:text-text-primary flex items-center gap-2">
                <span>🔒 Isolated Sandbox</span>
                <span className="text-[10px] font-semibold bg-accent text-white px-1.5 py-0.5 rounded font-sans">
                  Recommended
                </span>
              </div>
              <p className="text-xs text-text-secondary-light dark:text-text-secondary leading-relaxed">
                Every conversation is completely independent. The bot cannot
                access other users' data, memory, or tasks. Essential for public
                customer support.
              </p>
            </button>

            <button
              type="button"
              onClick={() => {
                if (bot) {
                  transact(tx.bots[bot.id].update({ isolateUsers: false }));
                }
              }}
              className={`p-4 rounded-xl border text-left flex flex-col gap-2 transition-all cursor-pointer ${
                bot?.isolateUsers === false
                  ? "border-accent bg-accent/5 dark:bg-accent/10 shadow-md"
                  : "border-border-subtle-light dark:border-border-subtle hover:border-accent/50 bg-surface-overlay-light dark:bg-surface-overlay"
              }`}
            >
              <div className="font-bold text-sm text-text-primary-light dark:text-text-primary">
                👥 Shared Assistant
              </div>
              <p className="text-xs text-text-secondary-light dark:text-text-secondary leading-relaxed">
                The bot can access context across chats, manage contacts, shared
                projects, and schedule actions. Best for internal team
                coordination.
              </p>
            </button>
          </div>
          <div className="flex gap-2 justify-end mt-4">
            <Button
              className="bg-surface-overlay-light dark:bg-surface-overlay text-text-primary-light dark:text-text-primary hover:bg-border-subtle-light dark:hover:bg-border-subtle"
              onClick={() => setStep(2)}
            >
              Back
            </Button>
            <Button onClick={() => setStep(4)}>Next</Button>
          </div>
        </div>
      )}
      {step === 4 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-xl font-display font-bold text-text-primary-light dark:text-text-primary">
            Install skills
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary text-sm">
            Give your bot extra capabilities by installing ready-made skills
            from the Tank community marketplace.
          </p>
          <div className="mt-2 min-h-[250px] max-h-[400px] overflow-y-auto">
            {bot
              ? (
                <CommunitySkillsConfig
                  id={bot.id}
                  botId={bot.id}
                  hideCollapsible
                />
              )
              : (
                <div className="text-sm text-text-tertiary-light dark:text-text-tertiary">
                  Loading skills configuration...
                </div>
              )}
          </div>
          <div className="flex gap-2 justify-end mt-4">
            <Button
              className="bg-surface-overlay-light dark:bg-surface-overlay text-text-primary-light dark:text-text-primary hover:bg-border-subtle-light dark:hover:bg-border-subtle"
              onClick={() => setStep(3)}
            >
              Back
            </Button>
            <Button onClick={() => setStep(5)}>Next</Button>
          </div>
        </div>
      )}
      {step === 5 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-xl font-display font-bold text-text-primary-light dark:text-text-primary">
            Talk to your bot
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary">
            Send a message to your bot right here. You can skip this step and do
            it later.
          </p>
          <div>
            {bot && <TalkToBotSection bot={bot.aliceAndBotCredentials} />}
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              className="bg-surface-overlay-light dark:bg-surface-overlay text-text-primary-light dark:text-text-primary hover:bg-border-subtle-light dark:hover:bg-border-subtle"
              onClick={() => setStep(4)}
            >
              Back
            </Button>
            <Button onClick={() => setStep(6)}>Next</Button>
          </div>
        </div>
      )}
      {step === 6 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-xl font-display font-bold text-text-primary-light dark:text-text-primary">
            View conversations
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary">
            Open your bot's conversation history in a new tab.
          </p>
          <div>
            {bot && (
              <ViewConversationsButton
                groupId={bot.viewChatGroupId}
                label="Open conversations"
              />
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              className="bg-surface-overlay-light dark:bg-surface-overlay text-text-primary-light dark:text-text-primary hover:bg-border-subtle-light dark:hover:bg-border-subtle"
              onClick={() => setStep(5)}
            >
              Back
            </Button>
            <Button onClick={() => setStep(7)}>Next</Button>
          </div>
        </div>
      )}
      {step === 7 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-xl font-display font-bold text-text-primary-light dark:text-text-primary">
            Your bot is ready!
          </h2>
          <div className="text-sm text-text-secondary-light dark:text-text-secondary space-y-2">
            <p>
              Head over to your bot's settings to connect channels, add
              integrations, and customize its behavior. There's a lot to explore
              — have fun!
            </p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              className="bg-surface-overlay-light dark:bg-surface-overlay text-text-primary-light dark:text-text-primary hover:bg-border-subtle-light dark:hover:bg-border-subtle"
              onClick={() => setStep(6)}
            >
              Back
            </Button>
            <Button
              onClick={() => {
                showWidget.value = false;
                onFinish();
              }}
            >
              Go to bot settings
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const periodUsage = (
  count: number | undefined,
  billingPeriodStart: number | undefined,
  tier?: string | null,
) => isEntityInCurrentPeriod({ tier, billingPeriodStart }) ? (count ?? 0) : 0;

const agentRunsUsed = (
  agentRunCount: number | undefined,
  llmRunCount: number | undefined,
  imageGenerationCount: number | undefined,
  billingPeriodStart: number | undefined,
  tier?: string | null,
) =>
  periodUsage(
    isLlmBillingActive() ? llmRunCount : agentRunCount,
    billingPeriodStart,
    tier,
  ) +
  periodUsage(imageGenerationCount, billingPeriodStart, tier) *
    imageGenerationAgentRunMultiplier;

const isBotOverQuota = (bot: {
  billingUser?: {
    tier?: string;
    phone?: string | null;
    signupTimestamp?: number | null;
    email?: string | null;
    agentRunCount?: number;
    llmRunCount?: number;
    imageGenerationCount?: number;
    billingPeriodStart?: number;
  } | null;
}) => {
  const billingUser = bot.billingUser;
  if (!billingUser) return false;
  const tier = tierByName(billingUser.tier ?? "Free");
  const limit = tier.name === "Free"
    ? freeTierCap(billingUser)
    : tier.agentRunsPerMonth;
  const used = agentRunsUsed(
    billingUser.agentRunCount,
    billingUser.llmRunCount,
    billingUser.imageGenerationCount,
    billingUser.billingPeriodStart,
    billingUser.tier,
  );
  return used >= limit;
};

const Bot = ({
  id,
  headerActions,
  highlightedSection,
  onClearHighlightedSection,
}: {
  id: string;
  headerActions?: ComponentChildren;
  highlightedSection?: { title: string; category: Category } | null;
  onClearHighlightedSection: () => void;
}) => {
  const { data, isLoading, error } = useQuery({
    bots: { billingUser: {}, $: { where: { id } } },
  });
  const token = useAuth().user?.refresh_token;
  if (isLoading) return <PageSpinner />;
  if (error) return error.message;
  const [bot] = data.bots;
  if (!bot) return <BotNotFound />;
  const activeUser = useActiveUser();
  const { injectContext } = readInjectContextParams();
  const urlBotId = readUrlBotId();
  const [activeTab, setActiveTab] = useState<Category>(
    injectContext && id === urlBotId ? "automation" : "overview",
  );

  const handleSaveName = async (newName: string) => {
    try {
      await transact(tx.bots[bot.id].update({ name: newName }));
      toast.success("Name saved");
    } catch (err) {
      toast.error("Error: " + readableError(err));
    }
  };

  const quickActions = (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full gap-3">
      <EditableText
        value={bot.name}
        onSave={handleSaveName}
        className="cursor-pointer min-w-0 max-w-full"
        inputClassName={`text-lg font-bold w-full max-w-full ${inputBaseClass}`}
        placeholder="Untitled bot"
      >
        <span
          className="text-lg font-bold hover:text-accent-light dark:hover:text-accent transition-colors block truncate"
          title="Click to rename"
        >
          {bot.name || "Untitled bot"}
        </span>
      </EditableText>
      {headerActions && (
        <div className="flex items-center gap-2 shrink-0">
          {headerActions}
        </div>
      )}
    </div>
  );

  const sections = sectionMetadata.map(({ title, keywords, categories }) => ({
    title,
    keywords,
    categories,
    content: (() => {
      if (title === "Prompt & Instructions") {
        return <PromptConfig id={bot.id} />;
      }
      if (title === "Behavior & Settings") {
        return <BehaviorConfig id={bot.id} />;
      }
      if (title === "Admins") {
        return <AdminsConfig id={bot.id} />;
      }
      if (title === "Actions") {
        return (
          <Collapsible title="Actions">
            <div className="flex flex-col items-start gap-3">
              <TalkToBotSection bot={bot.aliceAndBotCredentials} />
              {activeUser && (
                <OpenWebChatButton
                  botPubKey={bot.aliceAndBotCredentials.publicSignKey}
                  userCreds={activeUser.aliceAndBotCredentials}
                />
              )}
              <InviteLink
                publicSignKey={bot.aliceAndBotCredentials.publicSignKey}
              />
              <ViewConversationsButton groupId={bot.viewChatGroupId} />
            </div>
          </Collapsible>
        );
      }
      if (title === "Usage") {
        return (
          <Collapsible title="Usage">
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex gap-4 text-text-secondary-light dark:text-text-secondary">
                <span>{bot.incomingMessageCount ?? 0} messages received</span>
                <span>{bot.agentRunCount ?? 0} agent runs</span>
                <span>{bot.llmRunCount ?? 0} LLM runs</span>
                <span>{bot.imageGenerationCount ?? 0} images</span>
                <span>
                  {((bot.promptTokenCount ?? 0) +
                    (bot.candidatesTokenCount ?? 0) +
                    (bot.thoughtsTokenCount ?? 0)).toLocaleString()} tokens
                </span>
              </div>
              {bot.billingUser?.email && (
                <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
                  Billed to {bot.billingUser.email}
                </div>
              )}
              <div className="flex items-center justify-between pt-2 border-t border-border-subtle-light dark:border-border-subtle">
                <span className="text-xs text-text-tertiary-light dark:text-text-tertiary">
                  Need more capacity or higher limits?
                </span>
                <a
                  href="/pricing"
                  className="text-xs font-medium text-accent-light dark:text-accent hover:underline"
                >
                  View Plans & Pricing →
                </a>
              </div>
            </div>
          </Collapsible>
        );
      }
      if (title === "Inject Context") {
        const {
          injectContext,
          network,
          conversationId: initialConversationId,
        } = readInjectContextParams();
        return token
          ? (
            <Collapsible title="Inject Context">
              <InjectContextByUserId
                botId={bot.id}
                initialNetwork={injectContext && id === readUrlBotId()
                  ? network as PreferredNetwork | ""
                  : undefined}
                initialConversationId={injectContext && id === readUrlBotId()
                  ? initialConversationId
                  : undefined}
              />
            </Collapsible>
          )
          : null;
      }
      if (title === "Scheduled Actions") {
        return (
          <Collapsible title="Scheduled Actions">
            <Tasks id={bot.id} />
          </Collapsible>
        );
      }
      if (title === "Threads") {
        return (
          <Collapsible title="Threads">
            <Threads id={bot.id} />
          </Collapsible>
        );
      }
      if (title === "Web") {
        return (
          <Collapsible title="Web">
            <WebEmbedSnippetWithInputs
              publicSignKey={bot.aliceAndBotCredentials.publicSignKey}
            />
          </Collapsible>
        );
      }
      if (title === "Alice & Bot Network") {
        return <AliceAndBotConfig id={bot.id} />;
      }
      if (title === "Telegram") {
        return <TelegramConfig id={bot.id} />;
      }
      if (title === "Slack") {
        return <SlackConfig id={bot.id} />;
      }
      if (title === "Microsoft Teams") {
        return <TeamsConfig id={bot.id} />;
      }
      if (title === "WhatsApp") {
        return <WhatsappPrivateConfig id={bot.id} />;
      }
      if (title === "WhatsApp for Business") {
        return <WhatsappForBusinessConfig id={bot.id} />;
      }
      if (title === "Facebook Messenger") {
        return <FacebookMessengerConfig id={bot.id} />;
      }
      if (title === "Instagram DM") {
        return <InstagramConfig id={bot.id} />;
      }
      if (title === "Forward Email") {
        return <ForwardEmailConfig id={bot.id} />;
      }
      if (title === "AgentMail") {
        return <AgentmailConfig id={bot.id} />;
      }
      if (title === "Resend") {
        return <ResendConfig id={bot.id} />;
      }
      if (title === "Gmail") {
        return <GmailConfig id={bot.id} />;
      }
      if (title === "Files") {
        return <Files id={bot.id} />;
      }
      if (title === "Custom Tools") {
        return <CustomToolsConfig id={bot.id} />;
      }
      if (title === "Custom Skills") {
        return <CustomSkillsConfig id={bot.id} />;
      }
      if (title === "Community Skills") {
        return <CommunitySkillsConfig id={bot.id} botId={bot.id} />;
      }
      if (title === "Custom Recorder Webhook") {
        return <CustomRecorderWebhookConfig id={bot.id} />;
      }
      if (title === "Bot API Tokens") {
        return token
          ? (
            <ApiTokensSection
              mode="bot"
              botId={bot.id}
              title="Bot API Tokens"
              description="Create bot-scoped tokens for automations and integrations that should only have access to this bot."
              token={token}
              createButtonLabel="Create bot token"
              createPlaceholder="Token name (e.g. prod-webhook)"
            />
          )
          : null;
      }
      if (title === "Environment") {
        return <SecretsConfig id={bot.id} botId={bot.id} />;
      }
      return (
        <div className="flex flex-col gap-3">
          <DeleteAllMemory
            botId={bot.id}
            botName={bot.name}
            onNuked={() => {}}
          />
          <DeleteUserMemory botId={bot.id} />
          <DeleteBotEntirely bot={bot} token={token} />
        </div>
      );
    })(),
  })).filter(({ content }) => content !== null) as SearchableSection[];

  useEffect(() => {
    if (!highlightedSection || highlightedSection.category === activeTab) {
      return;
    }
    setActiveTab(highlightedSection.category);
  }, [highlightedSection?.title, highlightedSection?.category, activeTab]);

  useEffect(() => {
    if (!highlightedSection || highlightedSection.category !== activeTab) {
      return;
    }
    globalThis.requestAnimationFrame(() => {
      document.getElementById(sectionDomId(highlightedSection.title))
        ?.scrollIntoView(
          { block: "start", behavior: "smooth" },
        );
    });
  }, [highlightedSection?.title, highlightedSection?.category, activeTab]);

  const visibleSections = sections.filter(({ categories }) =>
    categories.includes(activeTab)
  );

  const wellClass =
    "p-4 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-overlay-light dark:bg-surface-overlay";

  const navPanel = (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-1.5 md:flex-col md:gap-0">
        {categoryTabs.map(({ id, label }) => (
          <button
            type="button"
            key={id}
            onClick={() => {
              onClearHighlightedSection();
              setActiveTab(id);
            }}
            className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors text-left ${
              activeTab === id
                ? "bg-accent text-white"
                : "text-text-secondary-light dark:text-text-secondary hover:bg-surface-overlay-light dark:hover:bg-surface-overlay"
            }`}
            aria-pressed={activeTab === id}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-5 min-w-0 w-full mx-auto break-words">
      {activeTab === "overview" && isBotOverQuota(bot) && (
        <div className="p-4 rounded-xl border border-rose-300 bg-rose-50 dark:border-rose-900/50 dark:bg-rose-950/20 text-rose-900 dark:text-rose-200 text-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-base">⚠️</span>
            <div>
              <strong>Out of quota:</strong>{" "}
              This agent has reached its monthly runs quota and has stopped
              responding.
            </div>
          </div>
          <a
            href="/pricing"
            className="shrink-0 text-center px-3.5 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-semibold text-xs transition-colors"
          >
            Upgrade Plan
          </a>
        </div>
      )}
      <div className={wellClass}>{quickActions}</div>
      <div className="flex flex-col md:flex-row gap-5">
        <div
          className={`${wellClass} md:w-48 md:shrink-0 md:sticky md:top-4 md:self-start`}
        >
          {navPanel}
        </div>
        <div className="flex flex-col gap-5 min-w-0 flex-1">
          {highlightedSection && highlightedSection.category === activeTab && (
            <div className={`${wellClass} py-3`}>
              <div className="text-xs uppercase tracking-wide text-text-tertiary-light dark:text-text-tertiary">
                Jumped via Ctrl+K
              </div>
              <div className="text-sm text-text-primary-light dark:text-text-primary">
                {categoryLabel(highlightedSection.category)} /{" "}
                {highlightedSection.title}
              </div>
            </div>
          )}
          {visibleSections.map(({ title, content }) => (
            <div key={title} id={sectionDomId(title)}>{content}</div>
          ))}
        </div>
      </div>
    </div>
  );
};

const botPageSize = 25;

export const MyBots = () => {
  usePageMeta({
    title: "Dashboard | prompt2bot",
    description: "Manage your bots, integrations, and settings on prompt2bot.",
  });
  const activeUser = useActiveUser();
  const {
    data: botsData,
    isLoading: botsLoading,
    canLoadNextPage,
    loadNextPage,
  } = useInfiniteQuery(
    activeUser?.id
      ? {
        bots: {
          $: {
            where: { admins: activeUser.id },
            limit: botPageSize,
            order: { serverCreatedAt: "desc" },
          },
        },
      }
      : null,
  );
  const bots = botsData?.bots ?? [];
  const isActuallyLoading = botsLoading || !activeUser?.id || !botsData;
  const { query } = useLocation();
  const fromUrl = query?.botId || readUrlBotId();
  const selectedBotExists = bots.some(({ id }: { id: string }) =>
    id === selectedBot.value
  );
  useEffect(() => {
    if (fromUrl) {
      selectedBot.value = fromUrl;
    } else if (bots.length > 0 && (!selectedBot.value || !selectedBotExists)) {
      selectedBot.value = bots[0].id;
    }
  }, [fromUrl, selectedBot.value, bots.length > 0, selectedBotExists]);
  const [creatingNewBot, setCreatingNewBot] = useState(false);
  if (!activeUser) {
    return <PageSpinner />;
  }
  const userApiToken = useAuth().user?.refresh_token;

  useEffect(() => {
    if (canLoadNextPage) loadNextPage();
  }, [canLoadNextPage]);

  if (creatingNewBot) {
    return (
      <div className="flex justify-center items-start pt-10">
        <NewBotWizard
          onCancel={() => setCreatingNewBot(false)}
          onFinish={() => setCreatingNewBot(false)}
        />
      </div>
    );
  }
  const headerActions = (
    <>
      <button
        type="button"
        onClick={openBotSwitcher}
        title="Switch bot or jump to setting (Ctrl+K)"
        className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-border-subtle-light dark:border-border-subtle hover:border-accent/50 bg-surface-raised-light dark:bg-surface-raised text-text-primary-light dark:text-text-primary transition-colors cursor-pointer"
      >
        <span>Switch bot / Jump to...</span>
        <kbd className="hidden sm:inline text-xs px-1.5 py-0.5 rounded bg-surface-overlay-light dark:bg-surface-overlay border border-border-subtle-light dark:border-border-subtle text-text-secondary-light dark:text-text-secondary font-mono">
          Ctrl+K
        </kbd>
      </button>
      <Button
        disabled={!userApiToken}
        onClick={() => setCreatingNewBot(true)}
        className="shrink-0 bg-success hover:bg-success-hover"
      >
        + New Bot
      </Button>
    </>
  );

  const currentBotId = fromUrl || selectedBot.value || bots[0]?.id;

  return (
    <div className="flex flex-col gap-4 flex-grow min-h-0">
      <main className="flex-1 min-w-0 min-h-0 w-full p-4 overflow-auto flex-grow flex flex-col">
        {currentBotId
          ? (
            <Bot
              key={currentBotId}
              id={currentBotId}
              headerActions={headerActions}
              highlightedSection={highlightedSectionSignal.value}
              onClearHighlightedSection={() => {
                highlightedSectionSignal.value = null;
              }}
            />
          )
          : isActuallyLoading
          ? <PageSpinner />
          : (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-text-secondary-light dark:text-text-secondary text-center px-4">
              <p className="text-lg">You don't have any bots yet.</p>
              <Button
                onClick={() => setCreatingNewBot(true)}
                className="bg-success hover:bg-success-hover"
              >
                + Create your first bot
              </Button>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary mt-2">
                Created a bot on WhatsApp or Telegram?{" "}
                <a
                  href={settingsPath}
                  className="text-primary underline hover:text-primary-hover"
                >
                  Link your account in Settings
                </a>{" "}
                to manage it here.
              </p>
            </div>
          )}
      </main>
    </div>
  );
};
